import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, FolderOpen, Download, Upload, Workflow as WorkflowIcon, Clock, FileText, Trash2, Edit3, Star, Loader2, Zap, Shield, Code, Sparkles, ArrowRight, Users, TrendingUp, CheckCircle } from 'lucide-react';
import Logo from './Logo';
import { Workflow } from '../types/workflow';
import { CONSTANT_STEPS } from '../constants/stepTemplates';
import { slugify } from '../utils/slug';
import { cn } from '../utils/cn';
import ProjectSetupModal, { ProjectData } from './ProjectSetupModal';
import { useProject } from '../context/ProjectContext';
import { SUPPORTED_CHAINS } from '../utils/abiFetcher';

interface ProjectLandingProps {}

interface WorkflowProject {
  id: string;
  name: string;
  description?: string;
  workflow: Workflow;
  createdAt: Date;
  modifiedAt: Date;
  thumbnail?: string;
  starred?: boolean;
}

const ProjectLanding: React.FC<ProjectLandingProps> = () => {
  const navigate = useNavigate();
  const { setProject } = useProject();
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [projects, setProjects] = useState<WorkflowProject[]>(() => {
    // Load projects from localStorage
    try {
      const saved = localStorage.getItem('dsl-workflow-projects');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.map((p: any) => ({
          ...p,
          createdAt: new Date(p.createdAt),
          modifiedAt: new Date(p.modifiedAt)
        }));
      }
    } catch (error) {
      console.warn('Failed to load projects:', error);
    }
    return [];
  });

  const [showImportDialog, setShowImportDialog] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);


  const saveProjects = (newProjects: WorkflowProject[]) => {
    try {
      localStorage.setItem('dsl-workflow-projects', JSON.stringify(newProjects));
      setProjects(newProjects);
    } catch (error) {
      console.warn('Failed to save projects:', error);
    }
  };

  const handleProjectSetup = async (projectData: ProjectData) => {
    setIsCreatingProject(true);
    
    try {
      // Set project context
      const chainConfig = SUPPORTED_CHAINS.find(chain => chain.chainId === projectData.chainId)!;
      const slugName = slugify(projectData.name);
      setProject({
        name: slugName,
        contractAddress: projectData.contractAddress,
        chainId: projectData.chainId,
        chainConfig: chainConfig,
        contractABI: projectData.abi,
        selectedFunction: projectData.selectedFunction,
        isLoading: false
      });

      // Create default workflow with project data using new DSL structure
      const defaultWorkflow: Workflow = {
        chain_id: projectData.chainId,
        sender: "{{ENV.SENDER_ADDRESS}}",
        delegate: "{{TRANSACTION_INTENT_DELEGATE}}",
        attestor: "{{ENV.ATTESTOR_IMAGE}}",
        target: {
          contract: projectData.contractAddress || "{{ENV.TARGET_CONTRACT}}",
          function: projectData.selectedFunction || "",
          authData_result: "0x00",
          parameters: []
        },
        sponsor_execution_fee: true,
        value: "0",
        intent: {
          id: "{{TRANSACTION_INTENT_ID}}",
          signature: "{{USER_SIGNATURE}}",
          deadline: "{{TRANSACTION_INTENT_DEADLINE}}"
        },
        rpc_url: "${_SECRETS.rpcSepoliaURL}",
        bundler_url: "https://api.pimlico.io/v2/sepolia/rpc?apikey=${_SECRETS.pimlico-apikey}",
        paymaster_url: "https://api.pimlico.io/v2/sepolia/rpc?apikey=${_SECRETS.pimlico-apikey}",
        gas_limit: "100000",
        max_fee_per_gas: "20000000000",
        max_priority_fee_per_gas: "2000000000",
        workflow: {
          name: slugName,
          version: "v1.0.0",
          steps: []
        },
        dapp: {
          name: slugName,
          targetChainID: projectData.chainId,
          targetContractAddress: projectData.contractAddress || "",
          targetFunctionSignature: projectData.selectedFunction || ""
        }
      };

      const newProject: WorkflowProject = {
        id: `project-${Date.now()}`,
        name: slugName,
        description: `Smart contract workflow for ${projectData.contractAddress}`,
        workflow: defaultWorkflow,
        createdAt: new Date(),
        modifiedAt: new Date(),
        starred: false
      };

      const updatedProjects = [newProject, ...projects];
      saveProjects(updatedProjects);
      localStorage.setItem('current-project', JSON.stringify(newProject));
      
      // Simulate loading project
      setTimeout(() => {
        setIsCreatingProject(false);
        navigate('/studio');
      }, 2000);
      
    } catch (error) {
      console.error('Failed to create project:', error);
      setIsCreatingProject(false);
    }
  };

  const createNewProject = () => {
    setShowSetupModal(true);
  };

  const deleteProject = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updatedProjects = projects.filter(p => p.id !== id);
    saveProjects(updatedProjects);
  };

  const toggleStar = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updatedProjects = projects.map(p => 
      p.id === id ? { ...p, starred: !p.starred } : p
    );
    saveProjects(updatedProjects);
  };

  const exportProject = (project: WorkflowProject, e: React.MouseEvent) => {
    e.stopPropagation();
    const exportData = {
      ...project,
      exportedAt: new Date().toISOString(),
      version: "1.0.0",
      format: "dsl-workflow-project"
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${slugify(project.name)}.dwf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleFileImport = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const result = e.target?.result;
        if (typeof result === 'string') {
          const importedData = JSON.parse(result);
          
          // Check if it's a project file (.dwf)
          if (importedData.format === 'dsl-workflow-project' && importedData.workflow) {
            const importedProject: WorkflowProject = {
              id: `imported-${Date.now()}`,
              name: `${importedData.name} (Imported)`,
              description: importedData.description || 'Imported project',
              workflow: importedData.workflow,
              createdAt: new Date(),
              modifiedAt: new Date(),
              starred: false
            };

            const updatedProjects = [importedProject, ...projects];
            saveProjects(updatedProjects);
            setShowImportDialog(false);
          }
          // Check if it's a workflow JSON file
          else if (importedData.workflow && Array.isArray(importedData.workflow.steps)) {
            const reconstructedWorkflow = reconstructWorkflowFromJson(importedData);
            
            const importedProject: WorkflowProject = {
              id: `imported-${Date.now()}`,
              name: `${reconstructedWorkflow.workflow.name || 'Imported Workflow'} (Imported)`,
              description: `Imported workflow with ${reconstructedWorkflow.workflow.steps.length} steps`,
              workflow: reconstructedWorkflow,
              createdAt: new Date(),
              modifiedAt: new Date(),
              starred: false
            };

            const updatedProjects = [importedProject, ...projects];
            saveProjects(updatedProjects);
            setShowImportDialog(false);
          } else {
            alert('Invalid file format. Please select a valid .dwf project file or .json workflow file.');
          }
        }
      } catch (error) {
        console.error('Failed to import file:', error);
        alert('Failed to import file. Please check the file format.');
      }
    };
    reader.readAsText(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = [...e.dataTransfer.files];
    const importFile = files.find(file => file.name.endsWith('.dwf') || file.name.endsWith('.json'));
    if (importFile) {
      handleFileImport(importFile);
    }
  };

  // System step names that should be filtered out (legacy from old workflow format)
  const LEGACY_SYSTEM_STEPS = [
    'prepare-authdata',
    'target-calldata', 
    'sca-calldata',
    'construct-evm' // Only filter the generic "construct-evm", not custom ones like "construct-property-analysis-evm"
  ];

  // Helper to identify if a step should be filtered out
  const isLegacySystemStep = (step: any): boolean => {
    // Only filter out steps by exact name match
    // Custom construct-evm steps with different names (e.g., "construct-property-analysis-evm") are kept
    return LEGACY_SYSTEM_STEPS.includes(step.name);
  };

  // JSON reconstruction function - only keeps custom steps, filters out system steps
  const reconstructWorkflowFromJson = (importedWorkflow: any): Workflow => {
    const steps = importedWorkflow.workflow?.steps || [];
    
    // Only keep custom steps - filter out all legacy system steps
    const customSteps = steps.filter((step: any) => !isLegacySystemStep(step));
    
    // Reconstruct custom steps with proper format
    const reconstructedSteps = customSteps.map((step: any) => ({
      type: step.type || 10,
      name: step.name,
      image: step.image,
      attestor: step.attestor,
      next: step.next || "prepare-authdata",
      config: step.config || {},
      inputs: reconstructInputsFromJson(step.inputs),
      outputs: (step.outputs || []).map((output: any) => ({
        name: output.name,
        type: output.type,
        value: output.value || output.name,
        required: output.required ?? true,
        export: output.export ?? true,
      })),
    }));
    
    // Return the workflow with proper structure, preserving all top-level fields
    // Ensure all required fields are present with defaults
    return {
      chain_id: importedWorkflow.chain_id || 11155111,
      sender: importedWorkflow.sender || "{{ENV.SENDER_ADDRESS}}",
      delegate: importedWorkflow.delegate || "{{TRANSACTION_INTENT_DELEGATE}}",
      attestor: importedWorkflow.attestor || "{{ENV.ATTESTOR_IMAGE}}",
      target: importedWorkflow.target || {
        contract: "{{ENV.TARGET_CONTRACT}}",
        function: "",
        authData_result: "0x00",
        parameters: []
      },
      sponsor_execution_fee: importedWorkflow.sponsor_execution_fee ?? true,
      value: importedWorkflow.value || "0",
      intent: importedWorkflow.intent || {
        id: "{{TRANSACTION_INTENT_ID}}",
        signature: "{{USER_SIGNATURE}}",
        deadline: "{{TRANSACTION_INTENT_DEADLINE}}"
      },
      rpc_url: importedWorkflow.rpc_url || "${_SECRETS.rpcSepoliaURL}",
      bundler_url: importedWorkflow.bundler_url || "https://api.pimlico.io/v2/sepolia/rpc?apikey=${_SECRETS.pimlico-apikey}",
      paymaster_url: importedWorkflow.paymaster_url || "https://api.pimlico.io/v2/sepolia/rpc?apikey=${_SECRETS.pimlico-apikey}",
      gas_limit: importedWorkflow.gas_limit || "100000",
      max_fee_per_gas: importedWorkflow.max_fee_per_gas || "20000000000",
      max_priority_fee_per_gas: importedWorkflow.max_priority_fee_per_gas || "2000000000",
      workflow: {
        name: importedWorkflow.workflow?.name || "imported-workflow",
        version: importedWorkflow.workflow?.version || "v1.0.0",
        steps: reconstructedSteps,
      },
    };
  };

  const reconstructInputsFromJson = (inputs: any): any[] => {
    if (Array.isArray(inputs)) {
      // Already in array format
      return inputs;
    } else if (inputs && typeof inputs === 'object') {
      // Convert object format back to array format for editing
      return Object.entries(inputs).map(([name, value]) => ({
        name,
        type: inferInputType(value),
        value,
        required: true,
        description: `${name} parameter`,
      }));
    }
    return [];
  };

  const inferInputType = (value: any): string => {
    if (typeof value === 'string') return 'string';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'boolean';
    if (Array.isArray(value)) return 'object[]';
    if (value && typeof value === 'object') return 'object';
    return 'string';
  };

  const sortedProjects = [...projects].sort((a, b) => {
    if (a.starred && !b.starred) return -1;
    if (!a.starred && b.starred) return 1;
    return b.modifiedAt.getTime() - a.modifiedAt.getTime();
  });

  return (
    <div className="min-h-screen bg-n8n-dark-950 text-n8n-dark-200 flex flex-col">
      {/* Sharp SaaS Navigation */}
      <nav className="bg-n8n-dark-800/95 backdrop-blur-xl border-b border-n8n-dark-600/50 sticky top-0 z-40 shadow-2xl">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Logo size="sm" showText={true} />
            </div>
            <div className="flex items-center space-x-3">
              <div className="hidden md:flex items-center space-x-1">
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content - flex-1 to take remaining space */}
      <main className="flex-1">
        {/* Projects Dashboard */}
        <section className="pt-8 pb-16 relative">
          <div className="max-w-7xl mx-auto px-6">
            {sortedProjects.length === 0 ? (
            /* Sharp Empty State */
            <div className="text-center py-20">
              <div className="relative w-32 h-32 mx-auto mb-12">
                <div className="absolute inset-0 bg-gradient-to-br from-n8n-primary-500/10 to-n8n-accent-500/10 rounded-2xl blur-xl"></div>
                <div className="relative w-full h-full bg-gradient-to-br from-n8n-dark-700 to-n8n-dark-800 rounded-2xl border border-n8n-dark-600/50 flex items-center justify-center shadow-2xl backdrop-blur-sm">
                  <WorkflowIcon size={48} className="text-n8n-primary-400" />
                </div>
              </div>
              <h3 className="text-3xl font-bold text-n8n-dark-50 mb-4 tracking-tight">Build Your First Workflow</h3>
              <p className="text-lg text-n8n-dark-300 mb-12 max-w-xl mx-auto">
                Create intelligent automation workflows for your dApps
              </p>
              
              {/* Sharp Quick Start Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-lg mx-auto mb-12">
                <button 
                  onClick={createNewProject}
                  className="group bg-gradient-to-br from-n8n-dark-800/90 to-n8n-dark-700/90 border border-n8n-dark-600 hover:border-n8n-primary-500/50 rounded-xl p-6 transition-all duration-300 hover:shadow-xl hover:scale-105 backdrop-blur-sm"
                >
                  <div className="bg-gradient-to-br from-n8n-primary-500/20 to-n8n-primary-600/20 w-10 h-10 rounded-lg flex items-center justify-center mb-3 mx-auto">
                    <Plus size={20} className="text-n8n-primary-400" />
                  </div>
                  <h4 className="font-semibold text-n8n-dark-50 mb-1 text-sm">Create Project</h4>
                  <p className="text-xs text-n8n-dark-400">Start with smart contracts</p>
                </button>
                
                <button 
                  onClick={() => setShowImportDialog(true)}
                  className="group bg-gradient-to-br from-n8n-dark-800/90 to-n8n-dark-700/90 border border-n8n-dark-600 hover:border-n8n-success-500/50 rounded-xl p-6 transition-all duration-300 hover:shadow-xl hover:scale-105 backdrop-blur-sm"
                >
                  <div className="bg-gradient-to-br from-n8n-success-500/20 to-n8n-success-600/20 w-10 h-10 rounded-lg flex items-center justify-center mb-3 mx-auto">
                    <Upload size={20} className="text-n8n-success-400" />
                  </div>
                  <h4 className="font-semibold text-n8n-dark-50 mb-1 text-sm">Import Project</h4>
                  <p className="text-xs text-n8n-dark-400">Upload existing workflows</p>
                </button>
              </div>
            </div>
          ) : (
            /* Projects Dashboard */
            <>
              {/* Sharp Dashboard Header */}
              <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8 gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-n8n-dark-50 mb-1 tracking-tight">Projects</h2>
                  <p className="text-sm text-n8n-dark-400 font-medium">Manage your automation workflows</p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2 bg-n8n-dark-800/80 border border-n8n-dark-600/50 rounded-lg px-3 py-1.5 backdrop-blur-sm">
                    <div className="w-1.5 h-1.5 bg-n8n-success-400 rounded-full"></div>
                    <span className="text-xs text-n8n-dark-300 font-medium">{projects.length}</span>
                  </div>
                  <button
                    onClick={() => setShowImportDialog(true)}
                    className="px-3 py-1.5 bg-n8n-dark-800/80 hover:bg-n8n-dark-700 border border-n8n-dark-600 hover:border-n8n-success-500/50 text-n8n-dark-200 hover:text-n8n-success-400 rounded-lg transition-all duration-200 text-sm font-medium flex items-center space-x-1.5"
                  >
                    <Upload size={14} />
                    <span>Import</span>
                  </button>
                  <button
                    onClick={createNewProject}
                    className="px-4 py-1.5 bg-gradient-to-r from-n8n-primary-500 to-n8n-primary-600 hover:from-n8n-primary-600 hover:to-n8n-primary-700 text-white rounded-lg transition-all duration-200 text-sm font-medium flex items-center space-x-1.5 shadow-lg hover:shadow-xl hover:scale-105"
                  >
                    <Plus size={14} />
                    <span>New</span>
                  </button>
                </div>
              </div>
              
              {/* Sharp Projects Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {sortedProjects.map((project) => (
                  <div
                    key={project.id}
                    onClick={() => {
                      localStorage.setItem('current-project', JSON.stringify(project));
                      navigate('/studio');
                    }}
                    className="group bg-gradient-to-br from-n8n-dark-800/90 to-n8n-dark-700/90 border border-n8n-dark-600/50 hover:border-n8n-primary-500/50 rounded-xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer hover:-translate-y-1 backdrop-blur-sm relative overflow-hidden h-72 flex flex-col"
                  >
                    {/* Sharp Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="bg-gradient-to-br from-n8n-primary-500/20 to-n8n-primary-600/20 w-12 h-12 rounded-lg flex items-center justify-center">
                        <WorkflowIcon size={24} className="text-n8n-primary-400" />
                      </div>
                      <button
                        onClick={(e) => toggleStar(project.id, e)}
                        className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-n8n-dark-600/50 rounded-lg transition-all duration-200"
                      >
                        <Star 
                          size={14} 
                          className={cn(
                            "transition-colors duration-200",
                            project.starred ? "text-yellow-400 fill-yellow-400" : "text-n8n-dark-400 hover:text-yellow-400"
                          )} 
                        />
                      </button>
                    </div>
                    
                    {/* Fixed Project Info */}
                    <div className="flex-1 mb-4">
                      <h3 className="font-bold text-n8n-dark-50 text-lg mb-2 group-hover:text-n8n-primary-200 transition-colors duration-300 tracking-tight leading-tight truncate">
                        {project.name}
                      </h3>
                      
                      <div className="h-10">
                        {project.description ? (
                          <p className="text-n8n-dark-400 text-sm leading-relaxed line-clamp-2">
                            {project.description}
                          </p>
                        ) : (
                          <p className="text-n8n-dark-500 text-sm italic">
                            No description
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Sharp Stats */}
                    <div className="flex items-center justify-between mb-4 text-xs">
                      <div className="flex items-center space-x-1.5 bg-n8n-dark-700/50 rounded-lg px-2.5 py-1.5">
                        <FileText size={12} className="text-n8n-primary-400" />
                        <span className="font-semibold text-n8n-dark-200">{project.workflow.workflow.steps.length}</span>
                        <span className="text-n8n-dark-400">steps</span>
                      </div>
                      
                      <div className="flex items-center space-x-1.5 bg-n8n-dark-700/50 rounded-lg px-2.5 py-1.5">
                        <Clock size={12} className="text-n8n-accent-400" />
                        <span className="font-semibold text-n8n-dark-200">
                          {project.modifiedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                    </div>

                    {/* Fixed Starred Indicator */}
                    <div className="h-6 mb-4 flex items-center">
                      {project.starred && (
                        <div className="flex items-center space-x-1">
                          <Star size={10} className="text-yellow-400 fill-yellow-400" />
                          <span className="text-xs text-yellow-400 font-medium">Starred</span>
                        </div>
                      )}
                    </div>

                    {/* Sharp Actions */}
                    <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <button
                        onClick={(e) => exportProject(project, e)}
                        className="flex-1 flex items-center justify-center space-x-1.5 p-2 bg-n8n-dark-600/50 hover:bg-n8n-primary-500/20 border border-n8n-dark-500/50 hover:border-n8n-primary-500/50 rounded-lg transition-all duration-200 text-n8n-dark-300 hover:text-n8n-primary-300"
                        title="Export project"
                      >
                        <Download size={12} />
                        <span className="text-xs font-medium">Export</span>
                      </button>
                      
                      <button
                        onClick={(e) => deleteProject(project.id, e)}
                        className="p-2 bg-red-900/20 hover:bg-red-900/40 border border-red-800/30 hover:border-red-600/50 rounded-lg transition-all duration-200 text-red-400 hover:text-red-300"
                        title="Delete project"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-n8n-dark-800/50 border-t border-n8n-dark-600/50 py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          
          <p className="text-sm text-n8n-dark-400">
            © 2025 KRNL Labs. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Import Dialog */}
      {showImportDialog && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-n8n-dark-800 rounded-2xl border border-n8n-dark-600 w-full max-w-lg shadow-2xl">
            <div className="p-6 border-b border-n8n-dark-600">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-n8n-primary-500/20 rounded-xl">
                  <Upload size={24} className="text-n8n-primary-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-n8n-dark-100">Import Project</h2>
                  <p className="text-n8n-dark-400 text-sm">Add an existing workflow to your workspace</p>
                </div>
              </div>
            </div>
            <div className="p-8">
              <div
                className={cn(
                  "border-2 border-dashed rounded-xl p-12 text-center transition-all duration-300",
                  dragOver
                    ? "border-n8n-primary-400 bg-n8n-primary-500/10 scale-105"
                    : "border-n8n-dark-600 hover:border-n8n-primary-500/50 hover:bg-n8n-dark-700/30"
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="mb-4">
                  <FolderOpen size={48} className={cn(
                    "mx-auto transition-colors duration-300",
                    dragOver ? "text-n8n-primary-400" : "text-n8n-dark-400"
                  )} />
                </div>
                <h3 className="text-lg font-semibold text-n8n-dark-200 mb-2">
                  {dragOver ? "Drop your file here" : "Choose your workflow file"}
                </h3>
                <p className="text-n8n-dark-400 mb-6">
                  Drag and drop your <code className="bg-n8n-dark-700 px-2 py-1 rounded text-n8n-primary-400 font-mono text-xs">.dwf</code> project or <code className="bg-n8n-dark-700 px-2 py-1 rounded text-n8n-success-400 font-mono text-xs">.json</code> workflow file here
                </p>
                <div className="flex items-center justify-center space-x-4">
                  <div className="flex-1 h-px bg-n8n-dark-600"></div>
                  <span className="text-n8n-dark-400 text-sm">or</span>
                  <div className="flex-1 h-px bg-n8n-dark-600"></div>
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-6 bg-gradient-to-r from-n8n-primary-500 to-n8n-primary-600 hover:from-n8n-primary-600 hover:to-n8n-primary-700 text-white px-6 py-3 rounded-xl transition-all duration-200 font-semibold hover:scale-105 shadow-xl"
                >
                  Browse Files
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".dwf,.json"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileImport(file);
                  }}
                />
              </div>
            </div>
            <div className="p-6 border-t border-n8n-dark-600 flex justify-end space-x-3">
              <button
                onClick={() => setShowImportDialog(false)}
                className="px-6 py-2.5 text-n8n-dark-300 hover:text-n8n-dark-200 hover:bg-n8n-dark-700 rounded-xl transition-all duration-200 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Setup Modal */}
      <ProjectSetupModal
        isOpen={showSetupModal}
        onClose={() => setShowSetupModal(false)}
        onComplete={handleProjectSetup}
      />

      {/* Project Loading Screen */}
      {isCreatingProject && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-n8n-dark-800 rounded-2xl border border-n8n-dark-600 p-8 shadow-2xl max-w-md w-full mx-4">
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <Loader2 size={48} className="text-n8n-primary-400 animate-spin" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Loading Project</h2>
                <p className="text-n8n-dark-300">
                  Setting up your workflow with contract ABI and configuring system nodes...
                </p>
              </div>
              <div className="w-full bg-n8n-dark-600 rounded-full h-2 overflow-hidden">
                <div className="bg-n8n-primary-500 h-2 rounded-full animate-pulse w-3/4"></div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProjectLanding;